import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { EquipmentInfosManagement } from '../components/EquipmentInfosManagement';
import {
  TEquipmentSchema,
  equipmentSchema,
} from '@/schemas/equipment/equipment.schema';
import {
  useGetEquipmentById,
  useUpdateEquipment,
} from '@/hooks/equipment.hooks';
import { useTranslation } from 'react-i18next';
import { getLanguage, getTranslatedItem } from '@/utils/language.utils';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { useGetTab } from '@/hooks/tab';
import { useListAllEquipments } from '@/hooks/equipment.hooks';
import {
  mapEquipmentInfosDefaultValues,
  mapEquipmentOptions,
} from '@/utils/map.utils';
import { EditEquipmentTab } from '@/type/ui/tab/equipmentTab';
import { EquipmentWhitelistBlacklistManagement } from '../components/EquipmentWhitelistBlacklistManagement';
import { useEffect } from 'react';
import { Equipment } from '@/api/entities/equipment';
import { EquipmentPhotoList } from '../components/EquipmentPhotoList';
import { isLocal, isProd } from '@/utils/constant.utils';
import { useAuth } from '@/hooks/auth.hooks';

export const EditEquipmentPage = () => {
  const { id, processingId } = useParams();
  const { limitedRoleLoggedInUserId } = useAuth();
  const { data, isLoading } = useGetEquipmentById(`${id}`);
  const { isLoading: isLoadingAllEquipments, data: allEquipments } =
    useListAllEquipments();
  const { t } = useTranslation('equipment');
  const navigate = useNavigate();

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditEquipmentTab.INFOS,
  });

  const translatedEquipment = getTranslatedItem(
    data?.data?.translatedEquipments
  );

  const {
    watch: watchEquipment,
    control: equipmentControl,
    register: registerEquipment,
    setValue: setEquipmentValue,
    setError: setEquipmentError,
    handleSubmit: handleSubmitEquipment,
    reset,
    formState: { errors: equipmentErrors, isSubmitting: isSubmittingEquipment },
  } = useForm<TEquipmentSchema>({
    resolver: zodResolver(equipmentSchema),
    defaultValues: {
      ...mapEquipmentInfosDefaultValues(data?.data, translatedEquipment),
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        ...mapEquipmentInfosDefaultValues(data?.data, translatedEquipment),
      });
    }
  }, [data, reset, translatedEquipment]);

  const { mutate: mutateUpdateEquipment, isPending: isPendingUpdateEquipment } =
    useUpdateEquipment({
      t,
      handleSuccess: () => navigate('/equipments'),
    });

  const onSubmitEquipmentInfos = (equipment: TEquipmentSchema) => {
    const { languageCode } = getLanguage();

    const dataWithLanguageCode = {
      ...equipment,
      languageCode,
    };

    const equipmentWithId = { id, ...dataWithLanguageCode };
    mutateUpdateEquipment(equipmentWithId);
  };

  return (
    <>
      <PageHeader
        backToLink="/equipments"
        title={t('theEquipment')}
        subTitle={translatedEquipment?.name || data?.data?.name}
        subTitleIconClassName="ki-size"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('equipmentList')} linkTo="/equipments" />
            <BreadcrumbItem
              text={translatedEquipment?.name || data?.data?.name}
              active
            />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('equipmentNotFound')}...`} />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:informations')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditEquipmentTab.INFOS}
              onChange={() => navigateToTab(EditEquipmentTab.INFOS)}
            />
            <TabItem
              text={t('whitelistBlacklist')}
              leftIcon={<i className="ki-outline ki-questionnaire-tablet" />}
              active={currentTab === EditEquipmentTab.WHITELIST_BLACKLIST}
              onChange={() =>
                navigateToTab(EditEquipmentTab.WHITELIST_BLACKLIST)
              }
            />
            {(isProd || isLocal) && !limitedRoleLoggedInUserId && (
              <>
                <TabItem
                  text={t('common:detections')}
                  leftIcon={<i className="ki-outline ki-abstract-25" />}
                  active={currentTab === EditEquipmentTab.DETECTIONS}
                  onChange={() => {
                    navigate(
                      processingId
                        ? `/equipment/edit/${id}/processing/${processingId}?tab=${EditEquipmentTab.DETECTIONS}`
                        : `/equipment/edit/${id}?tab=${EditEquipmentTab.DETECTIONS}`
                    );
                  }}
                />
                <TabItem
                  text={t('common:moderations')}
                  leftIcon={<i className="ki-outline ki-toggle-on" />}
                  active={currentTab === EditEquipmentTab.MODERATIONS}
                  onChange={() => {
                    navigate(
                      processingId
                        ? `/equipment/edit/${id}/processing/${processingId}?tab=${EditEquipmentTab.MODERATIONS}`
                        : `/equipment/edit/${id}?tab=${EditEquipmentTab.MODERATIONS}`
                    );
                  }}
                />
              </>
            )}
          </TabList>
          {currentTab === EditEquipmentTab.INFOS && (
            <EquipmentInfosManagement<TEquipmentSchema>
              t={t}
              data={!isLoading && data?.data}
              dataEquipmentOptions={
                !isLoadingAllEquipments &&
                mapEquipmentOptions(
                  allEquipments?.data as unknown as Equipment[]
                )
              }
              watch={watchEquipment}
              control={equipmentControl}
              register={registerEquipment}
              setValue={setEquipmentValue}
              setError={setEquipmentError}
              errors={equipmentErrors}
              submitDisabled={isSubmittingEquipment || isPendingUpdateEquipment}
              onSubmit={handleSubmitEquipment(onSubmitEquipmentInfos)}
            />
          )}
          {currentTab === EditEquipmentTab.WHITELIST_BLACKLIST && (
            <EquipmentWhitelistBlacklistManagement<TEquipmentSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchEquipment}
              control={equipmentControl}
              register={registerEquipment}
              setValue={setEquipmentValue}
              setError={setEquipmentError}
              errors={equipmentErrors}
              submitDisabled={isSubmittingEquipment || isPendingUpdateEquipment}
              onSubmit={handleSubmitEquipment(onSubmitEquipmentInfos)}
            />
          )}
          {currentTab === EditEquipmentTab.DETECTIONS && (
            <EquipmentPhotoList t={t} equipment={data?.data} />
          )}
          {currentTab === EditEquipmentTab.MODERATIONS && (
            <EquipmentPhotoList t={t} equipment={data?.data} />
          )}
        </div>
      )}
      {(isLoading || isSubmittingEquipment || isPendingUpdateEquipment) && (
        <Loader />
      )}
    </>
  );
};
